import type { SavedPage } from './db';

export interface SavedCalculationData<TInput, TResponse> {
  inputs: TInput;
  results?: TResponse;
  appName?: string;
  metadata?: SavedPage['metadata'];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function extractSavedCalculationData<TInput, TResponse>(data: unknown): SavedCalculationData<TInput, TResponse> | null {
  if (!isRecord(data)) {
    return null;
  }

  // Older saves may store raw inputs directly, while newer saves wrap them in
  // { inputs, results }. This helper normalizes both shapes for the page layer.
  if ('inputs' in data) {
    return {
      inputs: data.inputs as TInput,
      results: data.results as TResponse | undefined
    };
  }

  return {
    inputs: data as TInput
  };
}

export function extractSavedCalculationFromPage<TInput, TResponse>(savedPage: SavedPage | null | undefined): SavedCalculationData<TInput, TResponse> | null {
  if (!savedPage) {
    return null;
  }

  const calculation = extractSavedCalculationData<TInput, TResponse>(savedPage.data);
  if (!calculation) return null;

  return {
    ...calculation,
    appName: savedPage.appName,
    metadata: savedPage.metadata
  };
}

export async function loadSavedCalculation<TInput, TResponse>(calcId: number): Promise<SavedCalculationData<TInput, TResponse> | null> {
  // Centralize the IndexedDB lookup so page components only deal with typed data.
  const { db } = await import('./db');
  const savedPage = await db.savedPages.get(calcId);
  return extractSavedCalculationFromPage<TInput, TResponse>(savedPage);
}
